import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  dateLabel, periodLabel,
  UNIT_STATUS_LABEL, UNIT_STATUS_TONE, type Tone, type UnitStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import { Icon, IconNfc } from '@/components/common/NavIcons';
import { DistributionSummary } from './Distribution';
import { InvoiceHistoryCard } from './InvoiceHistory';
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';

/**
 * El estado de "Cobros abiertos" no sale de si hay filas en la tabla — sale
 * del mismo motor que usa la matriz (`unitBalance`, en `domain/matrix.ts`).
 * `SIN_SALDO` es la única lectura que puede pintarse en verde: las otras tres
 * dicen que hay algo que revisar, y `REQUIERE_CONCILIACION` dice explícitamente
 * que no hay evidencia suficiente para afirmar que se debe cero.
 */
type BalanceStatus = 'SIN_SALDO' | 'SALDO_PENDIENTE' | 'PAGO_PARCIAL' | 'REQUIERE_CONCILIACION';

const BALANCE_LABEL: Record<BalanceStatus, string> = {
  SIN_SALDO: 'Sin saldos pendientes',
  SALDO_PENDIENTE: 'Saldo pendiente',
  PAGO_PARCIAL: 'Pago parcial',
  REQUIERE_CONCILIACION: 'Requiere conciliación',
};
const BALANCE_TONE: Record<BalanceStatus, Tone> = {
  SIN_SALDO: 'ok',
  SALDO_PENDIENTE: 'warn',
  PAGO_PARCIAL: 'warn',
  REQUIERE_CONCILIACION: 'danger',
};
const BALANCE_HINT: Record<BalanceStatus, string> = {
  SIN_SALDO: 'Se revisó todo el historial de este consultorio y todo lo que correspondía cobrar está pagado.',
  SALDO_PENDIENTE: 'Hay cuentas por cobrar sin pagar.',
  PAGO_PARCIAL: 'Hay cuentas por cobrar pagadas solo en parte.',
  REQUIERE_CONCILIACION: 'Algún mes no tiene evidencia suficiente para afirmar cuánto se debe — puede faltar generar el cargo, o el histórico importado no alcanza para confiar en el monto.',
};

interface Unit {
  id: string; unit_code: string; name: string | null; status: UnitStatus;
  location_id: string | null; location_name: string | null;
  member_count: number; active_contracts: number;
  start_date: string | null; address: string | null; notes: string | null;
  doctors: string[]; service_name: string | null;
  /** El código del catálogo (`PAQ-PREMIUM`, `PAQ-REGULAR`, `LIMP-EXTRA`…).
   *  Es lo que decide cómo se pinta el paquete; ver `packageTier`. */
  service_code: string | null;
  outstanding_minor: number;
}

/**
 * El nivel del paquete, para pintarlo.
 *
 * Se deriva del **código** del catálogo y nunca del nombre: `services.name` es
 * texto libre que el admin edita desde Catálogo, y `service_name.includes(
 * 'Premium')` se caería en silencio el día que alguien renombre el paquete.
 *
 * El catálogo tiene cuatro servicios hoy (`PAQ-REGULAR`, `PAQ-PREMIUM`,
 * `LIMP-EXTRA`, `ATN-EMERG`) y es una tabla editable, así que esto es una
 * lista de excepción, no un `switch` exhaustivo: **solo Premium se destaca**, y
 * cualquier otro código —conocido, nuevo o nulo— cae en el tratamiento base.
 * Al revés (destacar todo lo que no sea Regular) un servicio nuevo entraría
 * pintado como Premium por descarte.
 */
type PackageTier = 'premium' | 'base';

function packageTier(code: string | null): PackageTier {
  return code === 'PAQ-PREMIUM' ? 'premium' : 'base';
}

/**
 * La galería de consultorios.
 *
 * El consultorio es la entidad operativa principal, así que su **número** es la
 * identidad y va primero y grande. Los médicos son gente asociada a él y van
 * debajo, como contexto — al revés de como se veía cuando el import metió el
 * nombre de una doctora en `service_units.name`.
 *
 * La búsqueda la resuelve el servidor y **llega a los médicos**: buscar
 * «Adriana González» devuelve su consultorio, no una lista vacía.
 */
/** Los cuatro segmentos del filtro. "Inactivos" junta pausado + terminado: son
 * la misma acción para quien filtra ("no está andando ahora mismo"), aunque
 * en el dominio sigan siendo dos estados distintos. */
type UnitFilter = 'ALL' | 'PROSPECT' | 'ACTIVE' | 'INACTIVE';

/**
 * El tono de cada segmento sale de `UNIT_STATUS_TONE`, el mismo mapa que
 * alimenta el `StatusBadge` de la tarjeta: así el contador verde del filtro y
 * el badge verde de la card dicen «activo» con el mismo color, en vez de que
 * el filtro invente una paleta propia.
 *
 * `INACTIVE` no existe en el dominio —junta `PAUSED` y `ENDED`—, así que toma
 * el tono de `ENDED` (neutro). Con el de `PAUSED` (warn) el segmento se leería
 * como una alerta, que no es lo que dice «no está andando ahora mismo».
 * `ALL` no es un estado y por eso no lleva tono.
 */
const UNIT_SEGMENTS: { key: UnitFilter; label: string; tone: Tone | null }[] = [
  { key: 'ALL', label: 'Todos', tone: null },
  { key: 'PROSPECT', label: 'Prospectos', tone: UNIT_STATUS_TONE.PROSPECT },
  { key: 'ACTIVE', label: 'Activos', tone: UNIT_STATUS_TONE.ACTIVE },
  { key: 'INACTIVE', label: 'Inactivos', tone: UNIT_STATUS_TONE.ENDED },
];

function matchesUnitFilter(u: Unit, filter: UnitFilter): boolean {
  if (filter === 'ALL') return true;
  if (filter === 'INACTIVE') return u.status === 'PAUSED' || u.status === 'ENDED';
  return u.status === filter;
}

/** Dos formas de ver la misma galería. Compacto sacrifica el detalle a favor
 * de barrer los 30+ consultorios de un vistazo; el detalle sigue estando a un
 * hover de distancia, en vez de perdido detrás de un clic. */
type Density = 'detailed' | 'compact';
const DENSITY_KEY = 'cleanblue:units-density';

/**
 * El conmutador de densidad se muestra con íconos, no con las palabras
 * «Detallado» y «Compacto»: la diferencia entre las dos vistas es cuántas
 * tarjetas entran por pantalla, y eso se dibuja mejor de lo que se escribe.
 *
 * Más cuadrados = más tarjetas = **Compacto** (la convención de Finder, Drive
 * y Figma). El `label` no desaparece del DOM: pasa a ser el nombre accesible
 * del botón (`aria-label` + `title`), porque un ícono sin etiqueta no es
 * autoexplicativo para un lector de pantalla ni para quien lo ve por primera vez.
 */
const DENSITY_OPTIONS: { key: Density; label: string; icon: 'densidad-detallada' | 'densidad-compacta' }[] = [
  { key: 'detailed', label: 'Detallado', icon: 'densidad-detallada' },
  { key: 'compact', label: 'Compacto', icon: 'densidad-compacta' },
];

/**
 * Las ilustraciones de marca que aparecen en la tarjeta (vista Detallado).
 *
 * Son **cuatro de las nueve** de `public/ilustraciones/`. No caben las nueve:
 * pesan 153 KB juntas y el presupuesto de la pantalla es 120 KB. Estas cuatro
 * son las de figura vertical —una o dos personas de pie, sin mueble ancho—,
 * que es la única forma que funciona en la franja alta y angosta del borde
 * derecho de la card; las de escena horizontal (`escritorio`, `cocina-
 * fregadero`, `estante`, `lavabo`, `ventana`) quedarían recortadas por la
 * mitad. Suman 65 KB.
 *
 * La ilustración es decorativa: va con `alt=""`, con dimensiones fijas y
 * `loading="lazy"`, y por debajo del texto en opacidad y en máscara.
 */
const CARD_ART = ['caminando-balde', 'plumero-escalera', 'trapeando', 'lampara-escalera'] as const;

/** La misma ilustración para el mismo consultorio, siempre. Sale del código y
 *  no de un random ni del índice en la lista: con un random cambiaría en cada
 *  render, y con el índice cambiaría al filtrar. */
function artFor(unitCode: string): string {
  let hash = 0;
  for (let i = 0; i < unitCode.length; i++) hash = (hash * 31 + unitCode.charCodeAt(i)) >>> 0;
  return CARD_ART[hash % CARD_ART.length]!;
}

/** «Torre Médica — Piso 5» → «Piso 5». No hay columna de piso en el esquema
 * — el edificio la codificó en el nombre de la ubicación — así que el filtro
 * se arma con lo que haya, y si algún día una ubicación no dice "Piso" en el
 * nombre, el filtro cae al nombre completo en vez de inventar un número. */
function floorLabel(locationName: string): string {
  return /piso\s*\S+/i.exec(locationName)?.[0] ?? locationName;
}

export function UnitsPage({ embedded }: { embedded?: boolean } = {}) {
  const [creating, setCreating] = useState(false);
  // Activos es lo que se quiere ver por defecto — Prospectos/Inactivos son la
  // excepción, no el punto de partida (dueño, 3-sep-2026).
  const [filter, setFilter] = useState<UnitFilter>('ACTIVE');
  const [floor, setFloor] = useState<string>('ALL');
  /* Los dos filtros (estado y piso) empiezan CERRADOS: mostrar los cuatro/tres
     segmentos todo el tiempo, uno al lado del otro, era ruido visual —mismo
     feedback para los dos ejes (dueño, 3-sep-2026). Arrancan como una sola
     píldora con la selección vigente; tocarla despliega las opciones, elegir
     una la vuelve a cerrar. */
  const [statusOpen, setStatusOpen] = useState(false);
  const [floorOpen, setFloorOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [density, setDensity] = useState<Density>(
    () => (localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'detailed'),
  );

  function changeDensity(next: Density) {
    setDensity(next);
    localStorage.setItem(DENSITY_KEY, next);
  }

  const params = new URLSearchParams();
  if (search.trim()) params.set('search', search.trim());
  const query = useApi<{ units: Unit[]; search: string | null }>(
    `/admin/units${params.toString() ? `?${params}` : ''}`,
  );

  const newUnitAction = (
    <Button variant="primary" onClick={() => setCreating(true)}>Nuevo consultorio</Button>
  );

  return (
    <>
      {embedded
        ? <div className="row mb-2"><span className="spacer" />{newUnitAction}</div>
        : (
          <PageHead
            title="Consultorios"
            subtitle="La unidad que se atiende. Un consultorio puede tener varios médicos y el cobro se reparte entre ellos."
            actions={newUnitAction}
          />
        )}

      <div className="row row--wrap mb-2">
        <div className="search-input" style={{ maxWidth: 320 }}>
          <input
            className="input"
            placeholder="Buscar por número, ubicación o doctor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="search-input__clear"
              aria-label="Limpiar búsqueda"
              onClick={() => setSearch('')}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <Async query={query} empty={(d) => d.units.length === 0 && (
        <Empty
          icon="consultorio"
          title={search ? `Nada coincide con «${search}»` : 'Todavía no hay consultorios'}
          hint={search
            ? 'La búsqueda mira el número, la ubicación y el nombre de los médicos.'
            : 'Cargá el primero o importá el listado desde el histórico.'}
          action={!search && (
            <Button variant="primary" onClick={() => setCreating(true)}>Nuevo consultorio</Button>
          )}
        />
      )}>
        {(data) => {
          const byStatus = data.units.filter((u) => matchesUnitFilter(u, filter));
          const counts: Record<UnitFilter, number> = {
            ALL: data.units.length,
            PROSPECT: data.units.filter((u) => u.status === 'PROSPECT').length,
            ACTIVE: data.units.filter((u) => u.status === 'ACTIVE').length,
            INACTIVE: data.units.filter((u) => u.status === 'PAUSED' || u.status === 'ENDED').length,
          };
          const activeIndex = UNIT_SEGMENTS.findIndex((s) => s.key === filter);

          // Los pisos que existen de verdad en los datos, no una lista fija:
          // el día que aparezca un tercero, el filtro lo muestra solo.
          const floors = Array.from(
            new Map(
              data.units
                .filter((u): u is Unit & { location_id: string; location_name: string } =>
                  u.location_id != null && u.location_name != null)
                .map((u) => [u.location_id, u.location_name]),
            ),
          ).sort(([, a], [, b]) => a.localeCompare(b));

          const units = byStatus.filter((u) => floor === 'ALL' || u.location_id === floor);

          return (
            <>
              {/* La barra de controles: tres ejes distintos, tres tratamientos.
                  El de estado lleva el color de `UNIT_STATUS_TONE`; el de piso
                  es la variante redondeada, para que se lea que pregunta
                  «dónde» y no «en qué estado»; el de densidad es solo íconos.
                  Los tres siguen siendo el mismo `.segmented`, con la pill
                  posicionada por `calc()` a partir de `--seg-n` / `--seg-i`.
                  Y los tres exponen ahora `aria-pressed`, que es la deuda de
                  §2.3 de la auditoría de UI/UX: hasta hoy el activo se
                  comunicaba solo con una clase CSS. */}
              <div className="row row--wrap mb-3" style={{ alignItems: 'center', gap: 10 }}>
                {statusOpen ? (
                  <div
                    className="segmented segmented--status"
                    role="group"
                    aria-label="Filtrar por estado"
                    style={{ '--seg-n': UNIT_SEGMENTS.length, '--seg-i': activeIndex } as React.CSSProperties}
                  >
                    <div className="segmented__pill" />
                    {UNIT_SEGMENTS.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        aria-pressed={filter === s.key}
                        className={`segmented__opt${s.tone ? ` segmented__opt--${s.tone}` : ''}${filter === s.key ? ' is-active' : ''}`}
                        onClick={() => { // HAPTIC_FEEDBACK
                          if (filter !== s.key) { haptic.light(); setFilter(s.key); }
                          setStatusOpen(false);
                        }}
                      >
                        {s.label}
                        <span className="segmented__count">{counts[s.key]}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="chip-filtro"
                    onClick={() => { haptic.light(); setStatusOpen(true); }} // HAPTIC_FEEDBACK
                  >
                    {UNIT_SEGMENTS.find((s) => s.key === filter)?.label}
                    <span className="segmented__count">{counts[filter]}</span>
                    <Icon name="chevron-der" size={14} />
                  </button>
                )}

                {floors.length > 1 && (
                  floorOpen ? (
                    <div
                      className="segmented segmented--place"
                      role="group"
                      aria-label="Filtrar por piso"
                      style={{ '--seg-n': floors.length + 1, '--seg-i': floors.findIndex(([id]) => id === floor) + 1 } as React.CSSProperties}
                    >
                      <div className="segmented__pill" />
                      <button
                        type="button"
                        aria-pressed={floor === 'ALL'}
                        className={`segmented__opt${floor === 'ALL' ? ' is-active' : ''}`}
                        onClick={() => { // HAPTIC_FEEDBACK
                          if (floor !== 'ALL') { haptic.light(); setFloor('ALL'); }
                          setFloorOpen(false);
                        }}
                      >
                        Todos
                      </button>
                      {floors.map(([id, name]) => (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={floor === id}
                          className={`segmented__opt${floor === id ? ' is-active' : ''}`}
                          onClick={() => { // HAPTIC_FEEDBACK
                            if (floor !== id) { haptic.light(); setFloor(id); }
                            setFloorOpen(false);
                          }}
                        >
                          {floorLabel(name)}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="chip-filtro"
                      onClick={() => { haptic.light(); setFloorOpen(true); }} // HAPTIC_FEEDBACK
                    >
                      {floor === 'ALL' ? 'Todos' : floorLabel(floors.find(([id]) => id === floor)?.[1] ?? '')}
                      <Icon name="chevron-der" size={14} />
                    </button>
                  )
                )}

                <span className="spacer" />

                <div
                  className="segmented segmented--icons"
                  role="group"
                  aria-label="Densidad de la galería"
                  style={{ '--seg-n': DENSITY_OPTIONS.length, '--seg-i': DENSITY_OPTIONS.findIndex((d) => d.key === density) } as React.CSSProperties}
                >
                  <div className="segmented__pill" />
                  {DENSITY_OPTIONS.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      aria-pressed={density === d.key}
                      /* El texto se fue del botón, así que el nombre del control
                         tiene que viajar en `aria-label`; `title` lo repite para
                         quien usa el mouse y no reconoce el ícono. */
                      aria-label={d.label}
                      title={d.label}
                      className={`segmented__opt${density === d.key ? ' is-active' : ''}`}
                      onClick={() => { if (density !== d.key) haptic.light(); changeDensity(d.key); }} // HAPTIC_FEEDBACK
                    >
                      <Icon name={d.icon} size={17} />
                    </button>
                  ))}
                </div>
              </div>

              {units.length === 0 ? (
                <Empty
                  icon="consultorio"
                  title={`Ningún «${UNIT_SEGMENTS.find((s) => s.key === filter)?.label ?? 'Todos'}» coincide con «${search}»`}
                  hint="Probá con otro filtro de estado o de piso, o limpiá la búsqueda."
                  action={<Button onClick={() => { setFilter('ALL'); setFloor('ALL'); }}>Ver en todos los estados</Button>}
                />
              ) : density === 'compact' ? (
                <div className="unit-grid unit-grid--compact">
                  {units.map((u) => (
                    <UnitCardCompact key={u.id} unit={u} highlight={data.search} />
                  ))}
                </div>
              ) : (
                <div className="unit-grid">
                  {units.map((u) => (
                    <UnitCard key={u.id} unit={u} highlight={data.search} />
                  ))}
                </div>
              )}
            </>
          );
        }}
      </Async>

      {creating && <NewUnitModal onClose={() => setCreating(false)} />}
    </>
  );
}

/**
 * Una card.
 *
 * Muestra hasta tres médicos y resume el resto. No depende de `hover` para nada
 * importante: en un teléfono el puntero no existe, y lo que solo se ve al pasar
 * el mouse ahí no se ve nunca.
 */
function UnitCard({ unit, highlight }: { unit: Unit; highlight: string | null }) {
  return (
    <Link to={`/admin/consultorios/${unit.id}`} className="unit-card">
      {/* La ilustración de marca. Va como hermana del contenido y no como
          `background-image` para poder pedirle `loading="lazy"`: con 35
          consultorios en pantalla, un fondo CSS se descarga entero al montar.
          Es decorativa (`alt=""`), así que un lector de pantalla no la nombra;
          lo que identifica la tarjeta sigue siendo el número. */}
      <img
        className="unit-card__art"
        src={`/ilustraciones/${artFor(unit.unit_code)}.png`}
        alt=""
        /* Los mismos valores que el CSS, para que el atributo no mienta. */
        width={86}
        height={112}
        loading="lazy"
        decoding="async"
      />
      <div className="unit-card__head">
        <span className="unit-card__code">Consultorio {unit.unit_code}</span>
        <StatusBadge value={unit.status} labels={UNIT_STATUS_LABEL} tones={UNIT_STATUS_TONE} />
      </div>

      <div className="unit-card__where">
        {unit.location_name ?? 'Sin ubicación'}
        {/* Un alias legítimo se muestra; el nombre de una persona ya no vive acá. */}
        {unit.name && ` · ${unit.name}`}
      </div>

      <div className="unit-card__doctors">
        <DoctorsList doctors={unit.doctors} highlight={highlight} max={3} />
      </div>

      <div className="unit-card__foot">
        <PackageAndDebt unit={unit} />
      </div>
    </Link>
  );
}

/** Los médicos, con hasta `max` nombres y un resumen del resto. Compartido
 * entre la card grande y el popover compacto — solo cambia cuántos entran. */
function DoctorsList({ doctors, highlight, max }: {
  doctors: string[]; highlight: string | null; max: number;
}) {
  if (doctors.length === 0) return <span className="muted">Sin médicos asociados</span>;

  const shown = doctors.slice(0, max);
  const rest = doctors.length - shown.length;
  const matched = highlight
    ? doctors.find((d) => d.toLowerCase().includes(highlight.toLowerCase()))
    : null;

  return (
    <>
      {shown.map((d) => (
        <span key={d} className={matched === d ? 'unit-card__match' : undefined}>{d}</span>
      ))}
      {rest > 0 && <span className="muted">y {rest} más</span>}
    </>
  );
}

/**
 * El paquete y la deuda del pie de la tarjeta.
 *
 * Premium se distingue de Regular con el mismo idioma que ya usa la landing
 * pública para jerarquizarlos (`.landing-package--featured`): borde en el
 * celeste de marca. Acá se hereda ese idioma en escala de chip en vez de
 * inventar otro.
 *
 * La diferencia **no es solo color** —eso dejaría fuera a quien no lo
 * distingue—: Premium además es rectangular donde el resto es píldora, y va en
 * negrita. Grosor de borde, forma y peso se ven los tres en blanco y negro.
 *
 * `Sin paquete` sigue siendo texto gris sin chip: es una ausencia, no un
 * tercer nivel por debajo de Regular.
 */
function PackageAndDebt({ unit }: { unit: Unit }) {
  const tier = packageTier(unit.service_code);
  return (
    <>
      <span>
        {unit.service_name
          ? (
            <span className={`pkg-chip pkg-chip--${tier}`}>
              {unit.service_name}
            </span>
          )
          : <span className="muted small">Sin paquete</span>}
      </span>
      {unit.outstanding_minor > 0 && (
        <span className="small">
          Debe <Money value={unit.outstanding_minor} />
        </span>
      )}
    </>
  );
}

/**
 * La card compacta: solo el código y el piso. Todo lo demás vive en un
 * popover que aparece con `hover` (o `focus`, para teclado/touch) — la
 * densidad existe justamente para barrer 30 consultorios de un vistazo, así
 * que meterle de vuelta médicos y paquete a la card la volvería a agrandar.
 */
function UnitCardCompact({ unit, highlight }: { unit: Unit; highlight: string | null }) {
  return (
    <Link to={`/admin/consultorios/${unit.id}`} className="unit-mini">
      <span className={`unit-mini__dot unit-mini__dot--${UNIT_STATUS_TONE[unit.status]}`} aria-hidden="true" />
      <span className="unit-mini__code">{unit.unit_code}</span>
      <span className="unit-mini__floor">
        {unit.location_name ? floorLabel(unit.location_name) : 'Sin ubicación'}
      </span>

      <div className="unit-mini__panel" role="tooltip">
        <div className="unit-mini__panel-head">
          <span className="unit-card__code">Consultorio {unit.unit_code}</span>
          <StatusBadge value={unit.status} labels={UNIT_STATUS_LABEL} tones={UNIT_STATUS_TONE} />
        </div>
        <div className="unit-card__doctors">
          <DoctorsList doctors={unit.doctors} highlight={highlight} max={4} />
        </div>
        <div className="unit-card__foot">
          <PackageAndDebt unit={unit} />
        </div>
      </div>
    </Link>
  );
}

function NewUnitModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ unit_code: '', name: '', address: '', start_date: '' });
  const mutation = useApiMutation(
    () => api.post('/admin/units', {
      unit_code: form.unit_code,
      unit_type: 'CLINIC',
      name: form.name || undefined,
      address: form.address || undefined,
      start_date: form.start_date || undefined,
    }),
    ['/admin/units', '/admin/dashboard'],
    { onSuccess: () => { haptic.success(); onClose(); } }, // HAPTIC_FEEDBACK
  );

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title="Nuevo consultorio"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!form.unit_code || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Guardando…' : 'Crear'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Field label="Código" required hint="El número del consultorio: 601, 514, PB-3.">
        <input className="input" value={form.unit_code} onChange={set('unit_code')} autoFocus />
      </Field>
      <Field label="Nombre">
        <input className="input" value={form.name} onChange={set('name')} />
      </Field>
      <Field label="Dirección">
        <input className="input" value={form.address} onChange={set('address')} />
      </Field>
      <Field
        label="Inicio del servicio"
        hint="Si lo dejás en blanco queda como prospecto: todavía no es cliente."
      >
        <input className="input" type="date" value={form.start_date} onChange={set('start_date')} />
      </Field>
    </Modal>
  );
}

/* ── Detalle ──────────────────────────────────────────────────────────────── */

interface UnitDetail {
  unit: Unit;
  members: {
    id: string; profile_id: string; full_name: string; role: string;
    active_from: string; active_until: string | null; email: string | null; phone: string | null;
  }[];
  contracts: { id: string; service_name: string; status: string; start_date: string; base_price_minor: number }[];
  // `party_name` es quién paga; `billing_name_snapshot`, a nombre de quién se
  // factura. Coinciden casi siempre, y cuando no, la columna muestra al
  // pagador y deja el nombre fiscal debajo.
  open_charges: { id: string; billing_name_snapshot: string; party_name: string; due_minor: number; paid_minor: number; due_date: string; payment_status: string; period_start: string }[];
  open_charges_summary: {
    month_count: number; receivable_count: number; outstanding_minor: number; oldest_period: string | null;
  };
  fiscal_identity: {
    party_id: string; display_name: string; profile_id: string | null;
    legal_name: string | null; tax_id: string | null; tax_id_type: string | null;
    billing_email: string | null; phone: string | null; address: string | null;
  } | null;
  balance: { status: BalanceStatus };
  nfc_tokens: UnitNfcToken[];
}

interface UnitNfcToken {
  id: string; token_prefix: string; label: string | null; is_active: number;
  scan_count: number; last_scanned_at: string | null; revoked_at: string | null; created_at: string;
}

export function UnitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useApi<UnitDetail>(`/admin/units/${id}`);

  return (
    <Async query={query}>
        {({ unit, members, contracts, open_charges_summary: openSummary, balance, nfc_tokens: nfcTokens, fiscal_identity: fiscal }) => (
          <>
          {/* El encabezado habla el mismo idioma que la tarjeta de la galería
              —línea de agua, lavado azul, ilustración de marca— para que abrir
              un consultorio se sienta como acercarse a la misma tarjeta, no
              como cambiar de sistema. El número va en una placa, como en la
              puerta del consultorio real (pedido del dueño, 27-ago-2026: el
              número suelto en texto plano «podría ser más estético»). */}
          <header className="unit-hero">
            <img
              className="unit-hero__art"
              src={`/ilustraciones/${artFor(unit.unit_code)}.png`}
              alt=""
              width={86}
              height={112}
              loading="lazy"
              decoding="async"
            />
            <Link to="/admin/clientes" className="btn unit-hero__back" title="Volver" aria-label="Volver a Clientes">
              <Icon name="volver" />
            </Link>
            <h1 className="unit-hero__plate">{unit.unit_code}</h1>
            <div className="unit-hero__text">
              <div className="unit-hero__eyebrow">
                Consultorio{unit.name ? ` · ${unit.name}` : ''}
              </div>
              <p className="unit-hero__where">
                {unit.location_name ?? 'Sin ubicación'}
                {' '}
                <StatusBadge value={unit.status} labels={UNIT_STATUS_LABEL} tones={UNIT_STATUS_TONE} />
              </p>
            </div>
          </header>

          {/* Contexto operativo primero. Esta grilla termina acá: las tarjetas
              secundarias no vuelven a empujar el estado mensual hacia abajo. */}
          <div className="unit-context">
            <Card title="Médicos" flush>
              {members.length === 0 ? (
                <Empty icon="doctor" title="Todavía no hay médicos asociados" />
              ) : (
                <Table head={<tr><th>Nombre</th><th>Rol</th><th>Desde</th><th>Contacto</th></tr>}>
                  {members.map((m) => (
                    <tr key={m.id} style={m.active_until ? { opacity: 0.55 } : undefined}>
                      <td className="strong">
                        <Cell sub={m.active_until ? `Salió el ${dateLabel(m.active_until)}` : undefined}>
                          <Link to={`/admin/doctores/${m.profile_id}`}>{m.full_name}</Link>
                        </Cell>
                      </td>
                      <td>{m.role === 'UNIT_ADMIN' ? 'Administra' : 'Miembro'}</td>
                      <td className="small muted">{dateLabel(m.active_from)}</td>
                      <td className="small muted">{m.email ?? m.phone ?? '—'}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>

            <div className="unit-context__billing">
              <Card title="Paquetes">
                {contracts.length === 0 ? (
                  <p className="muted small">Sin paquetes. Un consultorio sin paquete no genera cobros.</p>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {contracts.map((s) => (
                      <div key={s.id} className="row">
                        <div>
                          <Link to={`/admin/contratos/${s.id}`} className="strong">{s.service_name}</Link>
                          <span className="tbl__sub">Desde {dateLabel(s.start_date)}</span>
                        </div>
                        <div className="spacer" />
                        <Badge tone={s.status === 'ACTIVE' ? 'ok' : 'neutral'}>{s.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* El paquete activo (o, si no hay uno activo, el más reciente)
                  es el que define la distribución que importa mostrar acá. */}
              {(contracts.find((s) => s.status === 'ACTIVE') ?? contracts[0]) && (
                <DistributionSummary
                  subscriptionId={(contracts.find((s) => s.status === 'ACTIVE') ?? contracts[0])!.id}
                />
              )}
            </div>
          </div>

          <div className="unit-attention-card">
            <Card
              title="Atención inmediata"
              actions={
                <StatusBadge
                  value={balance.status}
                  labels={BALANCE_LABEL}
                  tones={BALANCE_TONE}
                  hints={BALANCE_HINT}
                />
              }
            >
              {balance.status === 'SIN_SALDO' && openSummary.month_count === 0 ? (
                <Empty icon="confirmar" title="Sin saldos pendientes" hint={BALANCE_HINT.SIN_SALDO} />
              ) : openSummary.month_count === 0 ? (
                <Empty
                  icon="consultorio"
                  title={BALANCE_LABEL[balance.status]}
                  hint={BALANCE_HINT[balance.status]}
                />
              ) : (
                <div className="unit-attention">
                  <div className="unit-attention__copy">
                    <strong>{openSummary.month_count} {openSummary.month_count === 1 ? 'mes con saldo' : 'meses con saldo'}</strong>
                    <Money value={openSummary.outstanding_minor} size="lg" />
                    <span className="small muted">
                      Más antiguo: {openSummary.oldest_period ? periodLabel(openSummary.oldest_period) : '—'}
                      {openSummary.receivable_count > openSummary.month_count
                        ? ` · ${openSummary.receivable_count} cuentas por cobrar` : ''}
                    </span>
                  </div>
                  <a className="btn btn--primary" href="#estado-mensual">Revisar meses</a>
                </div>
              )}
            </Card>
          </div>

          {/* La matriz usa todo el ancho: contiene ocho columnas en escritorio
              y tarjetas en móvil. No compite con una barra lateral. */}
          <section id="estado-mensual" className="unit-monthly">
            <InvoiceHistoryCard unitId={unit.id} />
          </section>

          <div className="unit-info-grid" aria-label="Información complementaria del consultorio">
            <Card title="Identidad fiscal del consultorio">
              {!fiscal?.profile_id ? (
                <Empty
                  icon="documento"
                  title="Sin identidad fiscal propia"
                  hint="Esto es independiente de quién administra el consultorio. Solo se usa si la modalidad elegida es «Paga el consultorio»."
                />
              ) : (
                <dl className="dl">
                  <dt>Razón social</dt><dd>{fiscal.legal_name ?? '—'}</dd>
                  <dt>Cédula</dt><dd>{fiscal.tax_id ?? '—'}</dd>
                  <dt>Correo fiscal</dt><dd>{fiscal.billing_email ?? '—'}</dd>
                  <dt>Teléfono</dt><dd>{fiscal.phone ?? '—'}</dd>
                </dl>
              )}
            </Card>

            <UnitNfcCard unitId={unit.id} tokens={nfcTokens} />

            <Card title="Ficha">
              <dl className="dl">
                <dt>Código</dt><dd>{unit.unit_code}</dd>
                <dt>Inicio</dt><dd>{unit.start_date ? dateLabel(unit.start_date) : '—'}</dd>
                <dt>Dirección</dt><dd>{unit.address ?? '—'}</dd>
              </dl>
              {unit.notes && <p className="small muted mt-2">{unit.notes}</p>}
            </Card>
          </div>
          </>
        )}
      </Async>
  );
}

/**
 * La etiqueta NFC de este consultorio.
 *
 * No reemplaza la lista global de Configuración —esa sigue siendo el
 * inventario completo—, esto es la vista puntual: qué etiqueta tiene *este*
 * consultorio, para emitirla o revocarla sin ir a buscarlo en la otra
 * pantalla. Emite con `service_unit_id` fijo, sin selector: acá no hace
 * falta elegir consultorio, ya se sabe cuál es.
 */
function UnitNfcCard({ unitId, tokens }: { unitId: string; tokens: UnitNfcToken[] }) {
  const [issued, setIssued] = useState<{ url: string } | null>(null);
  const active = tokens.find((t) => t.is_active === 1);

  const create = useApiMutation(
    () => api.post<{ url: string }>('/admin/nfc-tokens', { service_unit_id: unitId }),
    [`/admin/units/${unitId}`],
    { onSuccess: (data) => setIssued(data) },
  );

  const revoke = useApiMutation(
    (id: string) => api.post(`/admin/nfc-tokens/${id}/revoke`),
    [`/admin/units/${unitId}`],
  );

  return (
    <>
      <Card title={<h2 className="row"><IconNfc />Etiqueta NFC</h2>}>
        {active ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <div className="row">
              <Cell sub={active.last_scanned_at ? `Último escaneo ${dateLabel(active.last_scanned_at.slice(0, 10))}` : 'Sin escaneos todavía'}>
                <span className="mono strong">{active.token_prefix}…</span>
              </Cell>
              <div className="spacer" />
              <Badge tone="ok">{active.scan_count} escaneos</Badge>
            </div>
            {revoke.error && <Note tone="danger">{revoke.error.message}</Note>}
            <Button variant="danger" disabled={revoke.isPending} onClick={() => revoke.mutate(active.id)}>
              Revocar
            </Button>
          </div>
        ) : (
          <>
            {create.error && <Note tone="danger">{create.error.message}</Note>}
            <div className="unit-nfc-empty">
              <div className="unit-nfc-empty__copy">
                <span className="unit-nfc-empty__icon" aria-hidden="true"><IconNfc /></span>
                <span>
                  <strong>Sin etiqueta</strong>
                  <span className="tbl__sub">Este consultorio todavía no tiene una etiqueta NFC emitida.</span>
                </span>
              </div>
              <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>
                Emitir etiqueta
              </Button>
            </div>
          </>
        )}
      </Card>

      {issued && (
        <Modal
          title="Etiqueta emitida"
          onClose={() => setIssued(null)}
          footer={<Button variant="primary" onClick={() => setIssued(null)}>Listo</Button>}
        >
          <Note tone="warn">
            Guardá esta dirección ahora: es la única vez que se muestra. La base solo conserva su
            huella, así que ni nosotros podemos recuperarla después.
          </Note>
          <div className="row mt-2">
            <input className="input mono" readOnly value={issued.url} onFocus={(e) => e.target.select()} />
            <Button onClick={() => void navigator.clipboard.writeText(issued.url)}>Copiar</Button>
          </div>
        </Modal>
      )}
    </>
  );
}
